<template>
  <b-modal
    v-model="localValue"
    :title="modalTitle"
    size="lg"
    no-close-on-backdrop
    no-close-on-esc
    :hide-header-close="isProcessing"
    @hidden="resetModal">

    <div v-if="!isProcessing && !isCompleted">
      <!-- Confirmation Step -->
      <b-alert show variant="warning">
        <b-icon icon="exclamation-triangle"></b-icon>
        <strong>Confirmación Requerida</strong>
      </b-alert>

      <p>Estás a punto de iniciar un recálculo de costos para el siguiente rango de fechas:</p>

      <b-card bg-variant="light">
        <b-row>
          <b-col md="6">
            <strong>Fecha de Inicio:</strong><br>
            <span class="text-primary">{{ formatDate(dateRange.startDate) }}</span>
          </b-col>
          <b-col md="6">
            <strong>Fecha de Fin:</strong><br>
            <span class="text-primary">{{ formatDate(dateRange.endDate) }}</span>
          </b-col>
        </b-row>
      </b-card>

      <div class="mt-3">
        <h6>Este proceso:</h6>
        <ul>
          <li>Reprocessará todos los movimientos de inventario en el rango seleccionado</li>
          <li>Actualizará los costos usando las configuraciones actuales</li>
          <li>Puede tomar varios minutos dependiendo del volumen de datos</li>
          <li>Los reportes históricos reflejarán los nuevos cálculos</li>
        </ul>
      </div>

      <b-form-group>
        <b-form-checkbox v-model="confirmUnderstanding" :value="true" :unchecked-value="false">
          <strong>Confirmo que entiendo las implicaciones de este proceso</strong>
        </b-form-checkbox>
      </b-form-group>
    </div>

    <div v-else-if="isProcessing && !isCompleted">
      <!-- Processing Step -->
      <div class="text-center">
        <b-spinner style="width: 3rem; height: 3rem;" class="mb-3"></b-spinner>
        <h5>Recálculo en Progreso</h5>
        <p class="text-muted">{{ progressMessage }}</p>

        <b-progress
          :value="progress"
          :max="100"
          striped
          animated
          class="mb-3">
          <b-progress-bar :value="progress">{{ progress }}%</b-progress-bar>
        </b-progress>

        <div v-if="recalculationDetails">
          <b-card bg-variant="light" class="mt-3">
            <small>
              <strong>ID del Proceso:</strong> {{ recalculationDetails.id }}<br>
              <strong>Movimientos Afectados:</strong> {{ recalculationDetails.affected_movements_count || 0 }}<br>
              <strong>Estado:</strong> {{ getStatusText(recalculationDetails.status) }}
            </small>
          </b-card>
        </div>
      </div>
    </div>

    <div v-else-if="isCompleted">
      <!-- Completion Step -->
      <div class="text-center">
        <b-icon
          icon="check-circle"
          variant="success"
          style="font-size: 3rem;"
          class="mb-3">
        </b-icon>

        <h5 class="text-success">Recálculo Completado</h5>

        <div v-if="recalculationDetails">
          <b-card bg-variant="light" class="mt-3">
            <b-row>
              <b-col md="6">
                <strong>Movimientos Procesados:</strong><br>
                <span class="text-success">{{ recalculationDetails.affected_movements_count || 0 }}</span>
              </b-col>
              <b-col md="6">
                <strong>Tiempo Total:</strong><br>
                <span class="text-info">{{ calculateDuration() }}</span>
              </b-col>
            </b-row>
          </b-card>
        </div>

        <b-alert show variant="success" class="mt-3">
          <b-icon icon="info-circle"></b-icon>
          Los costos han sido recalculados exitosamente. Los reportes ya reflejan los nuevos valores.
        </b-alert>
      </div>
    </div>

    <template #modal-footer="{ hide }">
      <div v-if="!isProcessing && !isCompleted">
        <b-button variant="secondary" @click="hide">Cancelar</b-button>
        <b-button
          variant="danger"
          :disabled="!confirmUnderstanding"
          @click="startRecalculation">
          <b-icon icon="play"></b-icon>
          Iniciar Recálculo
        </b-button>
      </div>

      <div v-else-if="isProcessing && !isCompleted">
        <small class="text-muted">El proceso no puede ser cancelado una vez iniciado</small>
      </div>

      <div v-else-if="isCompleted">
        <b-button variant="primary" @click="hide">Cerrar</b-button>
      </div>
    </template>

  </b-modal>
</template>

<script>
import axios from "axios";

export default {
  name: "CostRecalculationModal",
  props: {
    value: {
      type: Boolean,
      default: false
    },
    dateRange: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      confirmUnderstanding: false,
      isProcessing: false,
      isCompleted: false,
      progress: 0,
      progressMessage: 'Iniciando recálculo...',
      recalculationDetails: null,
      statusPoller: null,
      startTime: null
    };
  },
  computed: {
    localValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    modalTitle() {
      if (this.isProcessing && !this.isCompleted) {
        return 'Recálculo de Costos en Progreso';
      } else if (this.isCompleted) {
        return 'Recálculo Completado';
      }
      return 'Confirmar Recálculo de Costos';
    }
  },
  watch: {
    value(newVal) {
      if (!newVal) {
        this.resetModal();
      }
    }
  },
  methods: {
    async startRecalculation() {
      this.isProcessing = true;
      this.startTime = new Date();
      this.progressMessage = 'Iniciando recálculo...';

      try {
        // Validate dates first
        if (!this.dateRange.startDate || !this.dateRange.endDate) {
          throw new Error('Fechas de inicio y fin son requeridas');
        }

        // Format dates to YYYY-MM-DD format for backend
        const startDate = new Date(this.dateRange.startDate).toISOString().split('T')[0]
        const endDate = new Date(this.dateRange.endDate).toISOString().split('T')[0]

        console.log('Sending recalculation request:', { start_date: startDate, end_date: endDate });

        const response = await axios.post('/cost/recalculate', {
          start_date: startDate,
          end_date: endDate
        });

        this.recalculationDetails = response.data;
        this.$emit('recalculation-started', response.data.id);

        // Start polling for status updates
        this.startStatusPolling(response.data.id);

      } catch (error) {
        this.handleError('Error al iniciar el recálculo', error);
      }
    },

    startStatusPolling(recalculationId) {
      this.statusPoller = setInterval(async () => {
        try {
          const response = await axios.get(`/cost/recalculation-status/${recalculationId}`);
          this.recalculationDetails = response.data;

          // Update progress based on status
          switch (response.data.status) {
            case 'pending':
              this.progress = 10;
              this.progressMessage = 'Preparando datos...';
              break;
            case 'processing':
              this.progress = Math.min(90, 30 + (this.calculateElapsedMinutes() * 10));
              this.progressMessage = 'Procesando movimientos de inventario...';
              break;
            case 'completed':
              this.progress = 100;
              this.progressMessage = 'Recálculo completado exitosamente';
              this.isCompleted = true;
              this.clearStatusPoller();
              this.$emit('recalculation-completed', response.data);
              break;
            case 'failed':
              this.handleError('El recálculo falló', new Error(response.data.error_message || 'Error desconocido'));
              this.clearStatusPoller();
              break;
          }
        } catch (error) {
          console.error('Error polling status:', error);
          // Continue polling - might be a temporary network issue
        }
      }, 2000); // Poll every 2 seconds
    },

    clearStatusPoller() {
      if (this.statusPoller) {
        clearInterval(this.statusPoller);
        this.statusPoller = null;
      }
    },

    calculateElapsedMinutes() {
      if (!this.startTime) return 0;
      return Math.floor((new Date() - this.startTime) / 60000);
    },

    calculateDuration() {
      if (!this.startTime || !this.recalculationDetails?.completed_at) {
        return 'N/A';
      }

      const endTime = new Date(this.recalculationDetails.completed_at);
      const durationMs = endTime - this.startTime;
      const minutes = Math.floor(durationMs / 60000);
      const seconds = Math.floor((durationMs % 60000) / 1000);

      if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      }
      return `${seconds}s`;
    },

    handleError(title, error) {
      this.isProcessing = false;
      this.clearStatusPoller();

      const message = error.response?.data?.error || error.message || 'Error desconocido';
      this.makeToast(title, message, 'danger');

      // Reset modal state
      this.resetModal();
    },

    resetModal() {
      this.confirmUnderstanding = false;
      this.isProcessing = false;
      this.isCompleted = false;
      this.progress = 0;
      this.progressMessage = 'Iniciando recálculo...';
      this.recalculationDetails = null;
      this.startTime = null;
      this.clearStatusPoller();
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    getStatusText(status) {
      const texts = {
        'pending': 'Pendiente',
        'processing': 'Procesando',
        'completed': 'Completado',
        'failed': 'Fallido'
      };
      return texts[status] || status;
    },

    makeToast(title, message, variant = 'info') {
      this.$bvToast.toast(message, {
        title: title,
        variant: variant,
        solid: true,
        autoHideDelay: 5000
      });
    }
  },

  beforeDestroy() {
    this.clearStatusPoller();
  }
};
</script>

<style scoped>
.modal-dialog {
  max-width: 600px;
}

.progress {
  height: 2rem;
}
</style>